import { Trans, useTranslation } from 'next-i18next';
import Link from 'next/link';
import { VFC } from 'react';
import { useToggle } from 'react-use';

import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import { MintBidWithUser } from '@/apis/aniftyBackend/mintAsks/getMintBidsByAskHash';
import CreationThumbnail from '@/components/CreationThumbnail';
import CustomButton from '@/components/CustomButton';
import LinkButton from '@/components/CustomButton/LinkButton';
import FloatingModal from '@/components/FloatingModal';
import routes from '@/config/routes';
import useWalletAddress from '@/hooks/useWalletAddress';

import styles from './BidStatus.module.scss';
import ClockIcon from './clock-icon.svg';

type Props = {
  creation: V2CreationData;
  auctionFinished: boolean;
  isDeadlineExceeded: boolean;
  highestBid?: MintBidWithUser | null;
};

const BidStatus: VFC<Props> = ({
  creation,
  auctionFinished,
  isDeadlineExceeded,
  highestBid,
}: Props) => {
  const { t, i18n } = useTranslation();

  const { currentAccountAddress } = useWalletAddress();

  const isWinning = highestBid?.signer.toLowerCase() === currentAccountAddress;

  const [showDetail, setShowDetail] = useToggle(false);

  if (auctionFinished) {
    if (isWinning) {
      // User has won the auction
      return (
        <>
          <CustomButton
            onClick={() => setShowDetail(true)}
            className={styles.congratulationsButton}
          >
            {t('auctionActivity.bidStatus.won')}
          </CustomButton>
          <FloatingModal
            isOpen={showDetail}
            onRequestClose={() => setShowDetail(false)}
          >
            <div className={styles.content}>
              <h2 className={`${styles.heading} ${styles.textOrange}`}>
                {t('auctionActivity.bidStatus.congratulations')} 🎉
              </h2>

              <div className={styles.thumbnail}>
                <CreationThumbnail creation={creation} size={140} />
              </div>

              <p>
                <Trans
                  i18nKey="auctionActivity.bidStatus.wonDescription"
                  components={{ strong: <strong /> }}
                  values={{
                    creatorName:
                      (i18n.language === 'ja' &&
                        creation.creator.displayNameJA) ||
                      creation.creator.displayName,
                  }}
                />
              </p>

              <Link href={routes.creationDetail(creation.creationID)} passHref>
                <LinkButton className={styles.confirmButton}>
                  {t('auctionActivity.activityCard.viewArtwork')}
                </LinkButton>
              </Link>
            </div>
          </FloatingModal>
        </>
      );
    }

    // User has lost the auction
    return (
      <>
        <CustomButton
          variant="whiteShadow"
          onClick={() => setShowDetail(true)}
          className={styles.missedButton}
        >
          {t('auctionActivity.bidStatus.missed')}
        </CustomButton>
        <FloatingModal
          isOpen={showDetail}
          onRequestClose={() => setShowDetail(false)}
        >
          <div className={styles.content}>
            <h2 className={styles.heading}>
              {t('auctionActivity.bidStatus.lostHeading')}
            </h2>

            <div className={styles.thumbnail}>
              <CreationThumbnail creation={creation} size={140} />
            </div>

            <p>
              <Trans
                i18nKey="auctionActivity.bidStatus.lostDescription"
                components={{ strong: <strong /> }}
                values={{
                  creatorName:
                    (i18n.language === 'ja' &&
                      creation.creator.displayNameJA) ||
                    creation.creator.displayName,
                }}
              />
            </p>

            <Link href={routes.marketplace} passHref>
              <LinkButton className={styles.confirmButton}>
                {t('auctionActivity.bidStatus.viewMarketplace')}
              </LinkButton>
            </Link>
          </div>
        </FloatingModal>
      </>
    );
  }

  if (isWinning || isDeadlineExceeded) {
    // User is winning the ongoing auction or already reached the time limit
    return (
      <div className={styles.waiting}>
        <ClockIcon />
        {t('auctionActivity.bidStatus.waiting')}
      </div>
    );
  }

  return (
    <Link href={routes.creationDetail(creation.creationID)} passHref>
      <LinkButton
        variant="primaryOutlined"
        className={styles.bidAgainButton}
        target="_blank"
      >
        {t('auctionActivity.bidStatus.bidAgain')}
      </LinkButton>
    </Link>
  );
};

export default BidStatus;
